/**
 * User-defined XYZ / WMTS basemaps (QGIS-style).
 *
 * XYZ — slippy-map URL template with `{z}/{x}/{y}` (or Esri `{z}/{y}/{x}`).
 * WMTS — OGC tiled map service. Users typically paste either:
 *   - a REST tile template with `{TileMatrix}/{TileRow}/{TileCol}`, or
 *   - a KVP GetTile URL that already contains `{z}/{x}/{y}` placeholders.
 *
 * Custom entries persist in localStorage and appear in the same gallery as
 * built-in basemaps. They never alter the live map tile pipeline until the
 * user selects them.
 */

export type CustomBasemapProtocol = 'xyz' | 'wmts';

export interface CustomBasemapRecord {
  readonly id: string;
  readonly label: string;
  readonly protocol: CustomBasemapProtocol;
  /** Normalized tile URL template using `{z}`, `{x}`, `{y}` (and optionally `{y}`/`{x}` swapped). */
  readonly urlTemplate: string;
  readonly attribution: string;
  readonly maxZoom: number;
  readonly createdAt: number;
}

export type CustomBasemapId = `custom:${string}`;

const STORAGE_KEY = 'geoviewer.customBasemaps';

/** In-memory mirror so custom basemaps work in tests / private-mode storage. */
let memoryStore: CustomBasemapRecord[] = [];

export function isCustomBasemapId(id: string): id is CustomBasemapId {
  return id.startsWith('custom:');
}

export function toCustomBasemapId(rawId: string): CustomBasemapId {
  return `custom:${rawId}`;
}

function newRawId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `bm-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Normalize a pasted XYZ or WMTS URL into a MapLibre/OL-friendly template.
 * Returns null when the URL has no usable tile placeholders.
 */
export function normalizeCustomTileTemplate(
  raw: string,
  protocol: CustomBasemapProtocol,
): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  let template = trimmed
    // WMTS REST common tokens → XYZ
    .replaceAll('{TileMatrix}', '{z}')
    .replaceAll('{TILEMATRIX}', '{z}')
    .replaceAll('{TileCol}', '{x}')
    .replaceAll('{TILECOL}', '{x}')
    .replaceAll('{TileRow}', '{y}')
    .replaceAll('{TILEROW}', '{y}')
    // Some providers use lowercase without braces variants already covered
    .replaceAll('{Level}', '{z}')
    .replaceAll('{Col}', '{x}')
    .replaceAll('{Row}', '{y}');

  // WMTS KVP often uses TILEMATRIX / TILEROW / TILECOL as query keys without braces.
  if (protocol === 'wmts') {
    template = template
      .replace(/(TILEMATRIX=)([^&]*)/i, (_, key: string, value: string) =>
        /\{z\}/i.test(value) ? `${key}${value}` : `${key}{z}`,
      )
      .replace(/(TILECOL=)([^&]*)/i, (_, key: string, value: string) =>
        /\{x\}/i.test(value) ? `${key}${value}` : `${key}{x}`,
      )
      .replace(/(TILEROW=)([^&]*)/i, (_, key: string, value: string) =>
        /\{y\}/i.test(value) ? `${key}${value}` : `${key}{y}`,
      );
  }

  const hasZ = template.includes('{z}');
  const hasX = template.includes('{x}');
  const hasY = template.includes('{y}');
  if (!hasZ || !hasX || !hasY) return null;
  return template;
}

export function validateCustomBasemapInput(input: {
  label: string;
  url: string;
  protocol: CustomBasemapProtocol;
}): { ok: true; urlTemplate: string } | { ok: false; error: string } {
  const label = input.label.trim();
  if (!label) return { ok: false, error: 'Enter a name for this basemap.' };

  const urlTemplate = normalizeCustomTileTemplate(input.url, input.protocol);
  if (!urlTemplate) {
    return {
      ok: false,
      error:
        input.protocol === 'wmts'
          ? 'WMTS URL must include tile placeholders ({z}/{x}/{y} or TileMatrix/TileCol/TileRow).'
          : 'XYZ URL must include {z}, {x}, and {y} placeholders.',
    };
  }

  try {
    // Absolute http(s) or same-origin absolute path both OK.
    if (/^https?:\/\//i.test(urlTemplate)) {
      // eslint-disable-next-line no-new
      new URL(urlTemplate.replaceAll('{z}', '0').replaceAll('{x}', '0').replaceAll('{y}', '0'));
    } else if (!urlTemplate.startsWith('/')) {
      return { ok: false, error: 'URL must start with https:// or /.' };
    }
  } catch {
    return { ok: false, error: 'URL is not valid.' };
  }

  return { ok: true, urlTemplate };
}

export function loadCustomBasemaps(): CustomBasemapRecord[] {
  if (typeof window !== 'undefined') {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed)) {
          memoryStore = parsed.filter(isValidRecord);
          return memoryStore;
        }
      }
    } catch {
      /* fall through to memory */
    }
  }
  return memoryStore.slice();
}

function isValidRecord(value: unknown): value is CustomBasemapRecord {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.label === 'string' &&
    (v.protocol === 'xyz' || v.protocol === 'wmts') &&
    typeof v.urlTemplate === 'string' &&
    typeof v.attribution === 'string' &&
    typeof v.maxZoom === 'number' &&
    typeof v.createdAt === 'number' &&
    v.urlTemplate.includes('{z}') &&
    v.urlTemplate.includes('{x}') &&
    v.urlTemplate.includes('{y}')
  );
}

function persist(records: CustomBasemapRecord[]): void {
  memoryStore = records.slice();
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
  } catch {
    /* ignore quota / private mode — memory mirror still works */
  }
}

/** Test helper: clear memory + localStorage custom basemap registry. */
export function clearCustomBasemapsForTests(): void {
  memoryStore = [];
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function getCustomBasemap(id: string): CustomBasemapRecord | undefined {
  const rawId = isCustomBasemapId(id) ? id.slice('custom:'.length) : id;
  return loadCustomBasemaps().find((r) => r.id === rawId);
}

export function addCustomBasemap(input: {
  label: string;
  protocol: CustomBasemapProtocol;
  url: string;
  attribution?: string;
  maxZoom?: number;
}): { ok: true; record: CustomBasemapRecord; basemapId: CustomBasemapId } | { ok: false; error: string } {
  const validated = validateCustomBasemapInput(input);
  if (!validated.ok) return validated;

  const record: CustomBasemapRecord = {
    id: newRawId(),
    label: input.label.trim(),
    protocol: input.protocol,
    urlTemplate: validated.urlTemplate,
    attribution: (input.attribution ?? '').trim() || 'Custom basemap',
    maxZoom: Math.min(22, Math.max(1, Math.round(input.maxZoom ?? 19))),
    createdAt: Date.now(),
  };

  const next = [...loadCustomBasemaps(), record];
  persist(next);
  return { ok: true, record, basemapId: toCustomBasemapId(record.id) };
}

export function removeCustomBasemap(id: string): boolean {
  const rawId = isCustomBasemapId(id) ? id.slice('custom:'.length) : id;
  const current = loadCustomBasemaps();
  const next = current.filter((r) => r.id !== rawId);
  if (next.length === current.length) return false;
  persist(next);
  return true;
}
